import { useCallback } from 'react'

import CUSTOM_SECTION_CONTAINER_NAMES from '@src/quince-site-common/src/constants/customSectionContainerNames'
import CUSTOM_SECTION_CONTAINER_TYPES from '@src/quince-site-common/src/constants/customSectionContainerTypes'
import Product from '@src/quince-site-common/src/domains/Product'
import Bundle, { PRODUCT_TYPES } from '@src/quince-site-common/src/domains/Product/BundleProduct'
import { TransparentPricingItem } from '@src/quince-site-common/src/domains/ProductTransparentPricing'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper, { CUSTOM_FIELD_NAMES } from '@src/quince-site-common/src/helpers/productHelper'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import BundlePageSectionProps from '@src/quince-site-pdp/src/domains/BundlePageSectionProps'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'

import ProductPageSectionProps from '../../../domains/ProductPageSectionProps'

import TransparentPricingBundle from './TransparentPricingBundle'
import TransparentPricingProduct from './TransparentPricingProduct'

export type TUniqueVariantPricing = Record<number, string>
export type TUniquePricingMap = Record<string, TransparentPricingItem>

export const pidAndSockDonationCostMap = {
  '145': 12.12,
  '532': 4.04,
  '2259': 13.21,
  '1139': 17.04,
  '531': 5.68,
  '318': 17.04,
  '1004': 5.68,
  '1255': 19.32,
  '733': 6.44,
  '1254': 22.32,
  '507': 7.44,
  '546': 17.04,
  '1005': 5.68,
  '2258': 17.04,
  '1192': 17.04,
  '1006': 5.68,
}

const TRACKING_CONTAINER_NAME = 'Product Transparent Pricing'

const TransparentPricing = (props: ProductPageSectionProps | BundlePageSectionProps) => {
  const product = 'bundle' in props ? props.bundle : props.product
  const selectedOptions = useAppSelector<ProductVariantOption[]>(pdpStateManager, pdpStateManager.getSelectedOptions)

  const getIsVisibleTrackParams = useCallback(() => {
    const productId = product.id
    const selectedColor = selectedOptions.filter(o => o.name == 'Color')?.[0]?.value
    const variants = productHelper.getVariantsForOptions(product, selectedOptions)
    return {
      action: 'product_transparent_pricing',
      value: true,
      context: {
        productId,
        selectedColor,
        contentContainerType: CUSTOM_SECTION_CONTAINER_TYPES.PRODUCT_DETAILS,
        contentContainerName: CUSTOM_SECTION_CONTAINER_NAMES.TRANSPARENT_PRICING,
        trackingId: 'product-transparent-pricing',
        contentContainerDisplayName: TRACKING_CONTAINER_NAME,
        productProperties: getProductPropertiesForTracking(product as Product, variants?.[0]?.svid?.toString()),
      },
    }
  }, [product.id, selectedOptions.filter(o => o.name == 'Color')?.[0]?.value])

  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(getIsVisibleTrackParams)

  return (
    <div ref={trackIsVisibleRef}>
      {'bundle' in props ? <TransparentPricingBundle {...props} /> : <TransparentPricingProduct {...props} />}
    </div>
  )
}

export const shouldHideTransparentPricing = (product: Product | Bundle) => {
  const VENDORS_WITHOUT_TRANSPARENT_PRICING = ['FIGB001']
  const PRODUCT_IDS_WITHOUT_TRANSPARENT_PRICING = ['7090547916970']
  let shouldHide = false
  if (PRODUCT_IDS_WITHOUT_TRANSPARENT_PRICING.includes(product.spid.toString())) {
    shouldHide = true
  }
  ;(product.vendorCodes || []).forEach(vc => {
    if (VENDORS_WITHOUT_TRANSPARENT_PRICING.includes(vc)) {
      shouldHide = true
    }
  })
  if (
    product.type !== PRODUCT_TYPES.BUNDLE &&
    productHelper.getIsTruthyCustomField(product, CUSTOM_FIELD_NAMES.HIDE_TRANSPARENT_PRICING)
  ) {
    shouldHide = true
  }
  return shouldHide
}

export default TransparentPricing
